// src/components/admin/TimeRangeSelector.js
import React, { useState } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';

const TimeRangeSelector = ({ onRangeChange, initialRange = 'thisMonth' }) => {
  const [selectedRange, setSelectedRange] = useState(initialRange);
  const [isCustom, setIsCustom] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  const presetRanges = [
    {
      id: 'today',
      label: 'Today',
      getValue: () => {
        const today = new Date();
        const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const end = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
        return {
          startDate: start.toISOString().split('T')[0],
          endDate: end.toISOString().split('T')[0],
          label: 'Today'
        };
      }
    },
    {
      id: 'yesterday',
      label: 'Yesterday',
      getValue: () => {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const start = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
        const end = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate() + 1);
        return {
          startDate: start.toISOString().split('T')[0],
          endDate: end.toISOString().split('T')[0],
          label: 'Yesterday'
        };
      }
    },
    {
      id: 'last7days',
      label: 'Last 7 Days',
      getValue: () => {
        const end = new Date();
        const start = new Date();
        start.setDate(end.getDate() - 7);
        return {
          startDate: start.toISOString().split('T')[0],
          endDate: end.toISOString().split('T')[0],
          label: 'Last 7 Days'
        };
      }
    },
    {
      id: 'last30days',
      label: 'Last 30 Days',
      getValue: () => {
        const end = new Date();
        const start = new Date();
        start.setDate(end.getDate() - 30);
        return {
          startDate: start.toISOString().split('T')[0],
          endDate: end.toISOString().split('T')[0],
          label: 'Last 30 Days'
        };
      }
    },
    {
      id: 'thisWeek',
      label: 'This Week',
      getValue: () => {
        const today = new Date();
        const start = new Date(today);
        start.setDate(today.getDate() - today.getDay()); // Start of week (Sunday)
        const end = new Date();
        return {
          startDate: start.toISOString().split('T')[0],
          endDate: end.toISOString().split('T')[0],
          label: 'This Week'
        };
      }
    },
    {
      id: 'lastWeek',
      label: 'Last Week',
      getValue: () => {
        const today = new Date();
        const start = new Date(today);
        start.setDate(today.getDate() - today.getDay() - 7); // Start of last week
        const end = new Date(start);
        end.setDate(start.getDate() + 7); // End of last week
        return {
          startDate: start.toISOString().split('T')[0],
          endDate: end.toISOString().split('T')[0],
          label: 'Last Week'
        };
      }
    },
    {
      id: 'thisMonth',
      label: 'This Month',
      getValue: () => {
        const today = new Date();
        const start = new Date(today.getFullYear(), today.getMonth(), 1);
        const end = new Date();
        return {
          startDate: start.toISOString().split('T')[0],
          endDate: end.toISOString().split('T')[0],
          label: 'This Month'
        };
      }
    },
    {
      id: 'lastMonth',
      label: 'Last Month',
      getValue: () => {
        const today = new Date();
        const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const end = new Date(today.getFullYear(), today.getMonth(), 1);
        return {
          startDate: start.toISOString().split('T')[0],
          endDate: end.toISOString().split('T')[0],
          label: 'Last Month'
        };
      }
    },
    {
      id: 'last3months',
      label: 'Last 3 Months',
      getValue: () => {
        const end = new Date();
        const start = new Date();
        start.setMonth(end.getMonth() - 3);
        return {
          startDate: start.toISOString().split('T')[0],
          endDate: end.toISOString().split('T')[0],
          label: 'Last 3 Months'
        };
      }
    },
    {
      id: 'last6months',
      label: 'Last 6 Months',
      getValue: () => {
        const end = new Date();
        const start = new Date();
        start.setMonth(end.getMonth() - 6);
        return {
          startDate: start.toISOString().split('T')[0],
          endDate: end.toISOString().split('T')[0],
          label: 'Last 6 Months'
        };
      }
    },
    {
      id: 'thisYear',
      label: 'This Year',
      getValue: () => {
        const today = new Date();
        const start = new Date(today.getFullYear(), 0, 1);
        const end = new Date();
        return {
          startDate: start.toISOString().split('T')[0],
          endDate: end.toISOString().split('T')[0],
          label: 'This Year'
        };
      }
    },
    {
      id: 'lastYear',
      label: 'Last Year',
      getValue: () => {
        const today = new Date();
        const start = new Date(today.getFullYear() - 1, 0, 1);
        const end = new Date(today.getFullYear(), 0, 1);
        return {
          startDate: start.toISOString().split('T')[0],
          endDate: end.toISOString().split('T')[0],
          label: 'Last Year'
        };
      }
    },
    {
      id: 'allTime',
      label: 'All Time',
      getValue: () => {
        const end = new Date();
        const start = new Date('2020-01-01'); // Adjust based on when you started
        return {
          startDate: start.toISOString().split('T')[0],
          endDate: end.toISOString().split('T')[0],
          label: 'All Time'
        };
      }
    }
  ];

  const handleRangeSelect = (rangeId) => {
    setSelectedRange(rangeId);
    setIsCustom(false);
    setShowDropdown(false);

    const range = presetRanges.find(r => r.id === rangeId);
    if (range && onRangeChange) {
      const rangeData = range.getValue();
      onRangeChange(rangeData);
    }
  };

  const handleCustomRange = () => {
    if (customStart && customEnd && onRangeChange) {
      setIsCustom(true);
      setSelectedRange('custom');
      setShowDropdown(false);
      onRangeChange({
        startDate: customStart,
        endDate: customEnd,
        label: `${customStart} to ${customEnd}`
      });
    }
  };

  const getCurrentLabel = () => {
    if (isCustom) {
      return `${customStart} to ${customEnd}`;
    }
    const range = presetRanges.find(r => r.id === selectedRange);
    return range ? range.label : 'Select Range';
  };

  return (
    <div className="relative">
      {/* Main Selector Button */}
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#3A98B9] focus:border-[#3A98B9]"
      >
        <Calendar className="h-4 w-4 mr-2 text-gray-400" />
        {getCurrentLabel()}
        <ChevronDown className="h-4 w-4 ml-2 text-gray-400" />
      </button>

      {/* Dropdown Menu */}
      {showDropdown && (
        <div className="absolute z-50 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="p-3">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Select Time Range</h4>

            {/* Preset Ranges */}
            <div className="space-y-1 mb-4">
              {presetRanges.map((range) => (
                <button
                  key={range.id}
                  onClick={() => handleRangeSelect(range.id)}
                  className={`w-full text-left px-3 py-2 text-sm rounded-md hover:bg-gray-100 ${
                    selectedRange === range.id && !isCustom
                      ? 'bg-[#3A98B9] text-white hover:bg-[#3A98B9]'
                      : 'text-gray-700'
                  }`}
                >
                  {range.label}
                </button>
              ))}
            </div>

            {/* Custom Range */}
            <div className="border-t border-gray-200 pt-3">
              <h5 className="text-xs font-medium text-gray-500 mb-2">Custom Range</h5>
              <div className="space-y-2">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-[#3A98B9] focus:border-[#3A98B9]"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">End Date</label>
                  <input
                    type="date"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-[#3A98B9] focus:border-[#3A98B9]"
                  />
                </div>
                <button
                  onClick={handleCustomRange}
                  disabled={!customStart || !customEnd}
                  className="w-full px-3 py-1 text-xs bg-[#3A98B9] text-white rounded hover:bg-[#2A87A8] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Apply Custom Range
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close */}
      {showDropdown && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  );
};

export default TimeRangeSelector;